import { Injectable } from '@angular/core';
import {
  Club,
  InscriptionPlayersPayment,
  LicensePlayerRate,
  LicenseType,
  NativeReturnRequest,
  PaymentMethod,
  PlayerInscriptionResponse,
  Season,
  Team,
  User,
} from '@models';

/** Selección devuelta por la página FindPlayers a la pantalla de inscripción. */
export type FindPlayersSelection = {
  playerResponse?: PlayerInscriptionResponse;
  team?: Team;
  licenseType: LicenseType;
};

export type InscriptionTeamData = {
  delegates: { userData: User; licenseRate?: LicensePlayerRate }[];
  players: { userData: User; licenseRate?: LicensePlayerRate }[];
  totals: { totalDelegatePrice: number; totalPlayerPrice: number };
  seasonId: string;
  scope: string;
};

@Injectable({
  providedIn: 'root',
})
export class InscriptionsPlayersService {
  /**
   * Puente entre la pantalla de inscripción y la página FindPlayers (que antes
   * era un modal). La inscripción fija el contexto (equipo + tipo de licencia)
   * antes de navegar; FindPlayers emite la selección y navega atrás.
   */
  findPlayersContext: {
    team: Team;
    licenseType: LicenseType;
    /** DNI buscado sin resultado, para prerrellenar el alta de usuario. */
    dni?: string;
  } | null = null;

  /**
   * Selección devuelta por FindPlayers / CreatePlayer, PENDIENTE de procesar.
   * La pantalla de inscripción la consume en `ionViewWillEnter` al reentrar
   * (fiable aunque la navegación de vuelta atraviese varias páginas), no vía un
   * Subject que dependía de que la suscripción estuviera viva en el instante
   * exacto de la emisión.
   */
  pendingSelection: FindPlayersSelection | null = null;

  emitFindPlayersSelection(selection: FindPlayersSelection): void {
    this.pendingSelection = selection;
  }

  hasPlayers(
    team: Team & { season: Season },
    dataToSave: Record<string, InscriptionTeamData>,
  ): boolean {
    const minPlayers = team.playersIds?.length
      ? 0
      : +((team.season as any).minPlayers ?? 1);
    const minDelegates = team.delegatesIds?.length ? 0 : 1;

    const hasPlayers = Object.values(dataToSave).some(
      (entry) => entry.players.length >= minPlayers,
    );
    const hasDelegates = Object.values(dataToSave).some(
      (entry) => entry.delegates.length >= minDelegates,
    );
    const hasAtLeastOne = Object.values(dataToSave).some(
      (entry) => entry.players.length > 0 || entry.delegates.length > 0,
    );

    return hasPlayers && hasDelegates && hasAtLeastOne;
  }

  prepareData(
    club: Club,
    tenantId: string,
    paymentMethod: PaymentMethod,
    dataToSave: Record<string, InscriptionTeamData>,
    team: Team & { season: Season },
    redirectUrls?: { successUrl?: string; cancelUrl?: string; nativeReturn?: NativeReturnRequest },
  ): InscriptionPlayersPayment {
    return {
      paymentMethod,
      tenantId,
      clubId: club.id,
      successUrl: redirectUrls?.successUrl,
      cancelUrl: redirectUrls?.cancelUrl,
      // #730: solo si viene. Nunca `nativeReturn: undefined` (el SDK lo enviaría como null).
      ...(redirectUrls?.nativeReturn ? { nativeReturn: redirectUrls.nativeReturn } : {}),
      teams: this.preparePlayers(dataToSave, team),
    };
  }

  preparePlayers(
    dataToSave: Record<string, InscriptionTeamData>,
    team: Team & { season: Season },
  ): {
    teamId: string;
    playersIds: string[];
    delegatesIds: string[];
    seasonId: string;
  }[] {
    return Object.entries(dataToSave).map(([teamId, data]) => ({
      teamId,
      playersIds: data.players.map((p) => p.userData.id),
      delegatesIds: data.delegates.map((d) => d.userData.id),
      seasonId: data.seasonId,
    }));
  }

  playerExistsInAnyTeam(
    dni: string,
    dataToSave: Record<string, InscriptionTeamData>,
  ): boolean {
    return Object.values(dataToSave).some((data) =>
      data.players.some((p) => p.userData.document === dni.toUpperCase()),
    );
  }

  playerExistsInAnyTeamOfSameScope(
    dni: string,
    team: Team,
    dataToSave: Record<string, InscriptionTeamData>,
  ): boolean {
    return Object.values(dataToSave)
      .filter((data) => data.scope === team.scope)
      .some((data) =>
        data.players.some((p) => p.userData.document === dni.toUpperCase()),
      );
  }

  delegateExistsInAnyTeam(
    dni: string,
    dataToSave: Record<string, InscriptionTeamData>,
  ): boolean {
    return Object.values(dataToSave).some((data) =>
      data.delegates.some((d) => d.userData.document === dni.toUpperCase()),
    );
  }

  async transferIsDisabled(seasonId: string): Promise<boolean> {
    return false;
  }
}
